import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatusForId } from '@ms/yammer-data-support';
import { CampaignDefaultThreadStarterContentType } from '@ms/yammer-graphql';
import createCachedSelector from 're-reselect';

import type { Campaign, YammerState } from '../../state/types';
import { getCampaignOrganizers } from '../campaignOrganizers/selectors';
import { getCampaignSponsors } from '../campaignSponsors/selectors';
import { createFindById, createFindByIdThrows } from '../findById';
import type { FindById, FindByIdThrows } from '../findById';
import { getCurrentUser } from '../users/selectors';

type GetStatusForCampaignId = (state: YammerState, campaignId: Campaign['id']) => EntityStatus;
export const getStatusForCampaignId: GetStatusForCampaignId = (state, campaignId) =>
  getStatusForId(state.campaigns, campaignId);

/**
 * @deprecated PERFORMANCE This returns an entity object and forces unnecessary re-renders.
 * This should be replaced by a `getCampaignField` selector.
 */
export const findCampaignById: FindById<'campaigns'> = createFindById('campaigns');

/**
 * @deprecated PERFORMANCE This returns an entity object and forces unnecessary re-renders.
 * This should be replaced by a `getCampaignFieldThrows` selector.
 */
export const findCampaignByIdThrows: FindByIdThrows<'campaigns'> = createFindByIdThrows('campaigns');

type GetCampaignDatabaseId = (state: YammerState, campaignId: Campaign['id']) => Campaign['databaseId'];
export const getCampaignDatabaseId: GetCampaignDatabaseId = (state, campaignId) =>
  findCampaignByIdThrows(state, campaignId).databaseId;

type GetTotalThreadStarters = (state: YammerState, campaignId: Campaign['id']) => number | undefined;
export const getTotalThreadStarters: GetTotalThreadStarters = (state, campaignId) => {
  const campaign = findCampaignById(state, campaignId);

  if (!campaign) {
    return undefined;
  }

  return campaign.totalThreadStarters;
};

type GetThreadStarterDefaultContentType = (
  state: YammerState,
  campaignId: Campaign['id']
) => CampaignDefaultThreadStarterContentType;
export const getThreadStarterDefaultContentType: GetThreadStarterDefaultContentType = (state, campaignId) => {
  const campaign = findCampaignById(state, campaignId);

  if (!campaign) {
    return CampaignDefaultThreadStarterContentType.NORMAL;
  }

  return campaign.threadStarterDefaultContentType;
};

type IsDefaultPostTypeQuestion = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isDefaultPostTypeQuestion: IsDefaultPostTypeQuestion = (state, campaignId) => {
  const defaultPostType = getThreadStarterDefaultContentType(state, campaignId);

  return defaultPostType === CampaignDefaultThreadStarterContentType.QUESTION;
};

type IsDefaultPostTypePraise = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isDefaultPostTypePraise: IsDefaultPostTypePraise = (state, campaignId) => {
  const defaultPostType = getThreadStarterDefaultContentType(state, campaignId);

  return defaultPostType === CampaignDefaultThreadStarterContentType.PRAISE;
};

type IsDefaultPostTypePoll = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isDefaultPostTypePoll: IsDefaultPostTypePoll = (state, campaignId) => {
  const defaultPostType = getThreadStarterDefaultContentType(state, campaignId);

  return defaultPostType === CampaignDefaultThreadStarterContentType.POLL;
};

interface GetDefaultPostTypeReturnTypeValue {
  readonly isDefaultPostTypeQuestion: boolean;
  readonly isDefaultPostTypePraise: boolean;
  readonly isDefaultPostTypePoll: boolean;
}
type GetDefaultPostType = (state: YammerState, campaignId?: Campaign['id']) => GetDefaultPostTypeReturnTypeValue;
export const getDefaultPostType: GetDefaultPostType = createCachedSelector(
  isDefaultPostTypeQuestion,
  isDefaultPostTypePraise,
  isDefaultPostTypePoll,
  (_state: YammerState, campaignId?: Campaign['id']) => campaignId,
  (isDefaultPostTypeQuestion, isDefaultPostTypePraise, isDefaultPostTypePoll, campaignId) => {
    if (!campaignId) {
      return {
        isDefaultPostTypeQuestion: false,
        isDefaultPostTypePraise: false,
        isDefaultPostTypePoll: false,
      };
    }

    return {
      isDefaultPostTypeQuestion,
      isDefaultPostTypePraise,
      isDefaultPostTypePoll,
    };
  }
)(
  (_state, campaignId) => campaignId || 'defaultKey' // Cache key function: ensures undefined campaignId doesn't cause issues
);

type CanStartThread = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canStartThread: CanStartThread = (state, campaignId) => {
  const campaign = findCampaignById(state, campaignId);

  if (!campaign) {
    return false;
  }

  return campaign.viewerCanStartThread;
};

type IsCurrentUserCampaignSponsor = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isCurrentUserCampaignSponsor: IsCurrentUserCampaignSponsor = (state, campaignId) => {
  const sponsors = getCampaignSponsors(state, campaignId);

  if (!sponsors || sponsors.length === 0) {
    return false;
  }

  const currentUser = getCurrentUser(state);

  return sponsors.includes(currentUser.id);
};

type IsCurrentUserCampaignCoOrganizer = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isCurrentUserCampaignCoOrganizer: IsCurrentUserCampaignCoOrganizer = (state, campaignId) => {
  const organizers = getCampaignOrganizers(state, campaignId);

  if (!organizers || organizers.length === 0) {
    return false;
  }

  const currentUser = getCurrentUser(state);

  return organizers.includes(currentUser.id);
};
